import { z } from "zod";
import { authoringCreatedBySchema, authoringPersistedIdSchema, authoringSlugSchema, } from "./authoring-shared.js";
export const skillGovernanceKindSchema = z.enum([
    "bundled_source",
    "organisation_policy",
    "governed_product",
]);
export const skillBindingKindSchema = z.enum([
    "view_sql",
    "catalog_narrative",
    "product_skill",
    "ambient_context",
]);
export const overlayClaimDestinationSchema = z.enum([
    ...skillBindingKindSchema.options,
    "drop",
]);
export const skillBindingActivationKindSchema = z.enum([
    "router",
    "explicit",
    "scoped",
]);
export const skillBindingAuditStatusSchema = z.enum([
    "pending",
    "approved",
    "rejected",
]);
export const skillBindingStatusSchema = z.enum([
    "staged",
    "active",
    "archived",
]);
const optionalScopedIdSchema = z.string().trim().min(1).max(200).optional();
export const skillBindingTargetSchema = z.object({
    pluginSlug: authoringSlugSchema.optional(),
    baseSkillSlug: authoringSlugSchema.optional(),
    productPublicationId: authoringPersistedIdSchema.optional(),
    productPublicationVersionId: authoringPersistedIdSchema.optional(),
    semanticViewId: authoringPersistedIdSchema.optional(),
    semanticViewVersionId: authoringPersistedIdSchema.optional(),
}).strict();
export const createSkillBindingInputSchema = z.object({
    id: authoringPersistedIdSchema,
    tenantSlug: z.string().trim().min(1).max(120),
    organisationId: optionalScopedIdSchema,
    skillTenantSlug: z.string().trim().min(1).max(120),
    skillId: authoringPersistedIdSchema,
    skillVersionId: authoringPersistedIdSchema,
    skillGovernanceKind: skillGovernanceKindSchema,
    bindingKind: skillBindingKindSchema,
    activationKind: skillBindingActivationKindSchema,
    target: skillBindingTargetSchema.default({}),
    maxContextChars: z.number().int().nonnegative().max(100_000),
    priority: z.number().int().min(-10_000).max(10_000).default(100),
    auditStatus: skillBindingAuditStatusSchema.default("pending"),
    auditDigest: z.string().trim().min(1).max(200).optional(),
    auditedBy: z.string().trim().min(1).max(200).optional(),
    auditedAt: z.number().int().nonnegative().optional(),
    status: skillBindingStatusSchema.default("staged"),
    metadata: z.record(z.string(), z.unknown()).default({}),
    createdBy: authoringCreatedBySchema,
    createdAt: z.number().int().nonnegative(),
}).strict().superRefine((binding, ctx) => {
    const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    const target = binding.target;
    const hasPluginTarget = target.pluginSlug !== undefined &&
        target.baseSkillSlug !== undefined;
    const hasProductTarget = target.productPublicationId !== undefined &&
        target.productPublicationVersionId !== undefined;
    const hasViewTarget = target.semanticViewId !== undefined &&
        target.semanticViewVersionId !== undefined;
    if (![binding.tenantSlug, "__mlx__"].includes(binding.skillTenantSlug)) {
        issue(["skillTenantSlug"], "A binding can use only tenant-owned or bundled system skills");
    }
    if (binding.skillTenantSlug === "__mlx__" &&
        binding.skillGovernanceKind !== "bundled_source") {
        issue(["skillGovernanceKind"], "System-owned skills must be classified as bundled sources");
    }
    if (binding.skillTenantSlug !== "__mlx__" &&
        binding.bindingKind === "product_skill" &&
        binding.skillGovernanceKind !== "governed_product") {
        issue(["skillGovernanceKind"], "Product bindings require governed product skills");
    }
    if (binding.skillTenantSlug !== "__mlx__" &&
        binding.bindingKind !== "product_skill" &&
        binding.skillGovernanceKind !== "organisation_policy") {
        issue(["skillGovernanceKind"], "Tenant non-product bindings require organisation policy skills");
    }
    if (binding.status === "active" && binding.auditStatus !== "approved") {
        issue(["status"], "Active bindings require an approved audit");
    }
    if (binding.auditStatus === "approved" &&
        (!binding.auditDigest || !binding.auditedBy || binding.auditedAt === undefined)) {
        issue(["auditStatus"], "Approved audits require digest, auditor, and timestamp");
    }
    if (binding.auditStatus === "pending" &&
        (binding.auditDigest || binding.auditedBy || binding.auditedAt !== undefined)) {
        issue(["auditStatus"], "Pending audits cannot carry adjudication fields");
    }
    if (binding.auditStatus === "rejected" &&
        (!binding.auditedBy || binding.auditedAt === undefined)) {
        issue(["auditStatus"], "Rejected audits require an auditor and timestamp");
    }
    if (binding.bindingKind === "view_sql") {
        if (binding.activationKind !== "scoped" || !hasViewTarget) {
            issue(["target"], "View SQL bindings require a scoped, version-pinned semantic view");
        }
        if (hasPluginTarget || hasProductTarget || binding.maxContextChars !== 0) {
            issue(["bindingKind"], "View SQL is a zero-context deterministic guardrail");
        }
    }
    if (binding.bindingKind === "catalog_narrative") {
        if (!["router", "explicit"].includes(binding.activationKind) || !hasPluginTarget) {
            issue(["target"], "Catalog narratives require a routed or explicit plugin skill target");
        }
        if (hasProductTarget || hasViewTarget || binding.maxContextChars === 0) {
            issue(["bindingKind"], "Catalog narratives are routed skills with a positive context budget");
        }
    }
    if (binding.bindingKind === "product_skill") {
        if (!["router", "explicit"].includes(binding.activationKind) ||
            !hasPluginTarget || !hasProductTarget || !binding.organisationId) {
            issue(["target"], "Product skills require routed plugin and product-version targets");
        }
        if (hasViewTarget || binding.maxContextChars === 0) {
            issue(["bindingKind"], "Product skills require a positive context budget");
        }
    }
    if (binding.bindingKind === "ambient_context") {
        if (binding.activationKind !== "scoped" ||
            hasPluginTarget || hasProductTarget || hasViewTarget ||
            Object.keys(target).length > 0 || binding.maxContextChars === 0) {
            issue(["bindingKind"], "Ambient context must be scoped, target-free, and budgeted");
        }
    }
});
